// src/pages/admin/CompanySummaryTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import {
  startOfDayMs,
  nextDayMs,
  FUTILE_CHARGE_AMOUNT,
  ADVANCED_EXPORT_COLUMNS,
  ADVANCED_EXPORT_OPTIONS,
  addrToText,
  tsToMs,
  inRange,
  fmtAU,
  fmtDateAU,
  esc,
  normalizeText,
  slugify,
  makeSiteSelectionKey,
  toNumber,
  firstDefined,
  getQty,
  fmtCurrency,
  parseQtyFromText,
  canonicalServiceLabel,
  isSilicaMaterialName,
  isTruthyPrice,
  componentSortMs,
  sumQtyArray,
  getDateRangeArray,
  summarizeWasteCharges,
} from "./companySummary/helpers";

export default function CompanySummaryTab({ company, companies = [] }) {
  const [companyId, setCompanyId] = useState(company?.id || "");
  const [companyName, setCompanyName] = useState(company?.name || "");
  const [allCompanies, setAllCompanies] = useState(companies);

  const [fromDate, setFromDate] = useState(() =>
    new Date().toLocaleDateString("en-CA")
  );
  const [toDate, setToDate] = useState(() =>
    new Date().toLocaleDateString("en-CA")
  );

  const [loading, setLoading] = useState(false);
  const [loanRows, setLoanRows] = useState([]);
  const [loadRows, setLoadRows] = useState([]);
  const [customers, setCustomers] = useState({});

  const [driverOptions, setDriverOptions] = useState([]);
  const [selectedDriverIds, setSelectedDriverIds] = useState([]);
  const [driverSearch, setDriverSearch] = useState("");
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedSiteKeys, setSelectedSiteKeys] = useState([]);
  const [siteSearch, setSiteSearch] = useState("");
  const [selectedExportColumns, setSelectedExportColumns] = useState(
    ADVANCED_EXPORT_COLUMNS
  );
  const [groupAdvancedBySite, setGroupAdvancedBySite] = useState(true);
  const [exportingAdvanced, setExportingAdvanced] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState({
    drivers: false,
    customers: false,
    sites: false,
    export: false,
  });

  useEffect(() => {
    if (allCompanies.length || company) return;
    (async () => {
      const snap = await getDocs(collection(db, "companies"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      setAllCompanies(list);
    })();
  }, [allCompanies.length, company]);

  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "customers"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const dict = {};
      list.forEach((c) => (dict[c.id] = c));
      setCustomers(dict);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "users"));
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => normalizeText(u.role) === "driver")
          .map((u) => ({
            id: u.id,
            name: u.name || u.displayName || u.fullName || u.email || u.id,
            email: u.email || "",
            search: normalizeText(
              [u.name, u.displayName, u.fullName, u.email, u.id].filter(Boolean).join(" ")
            ),
          }))
          .sort((a, b) => String(a.name || a.email || a.id).localeCompare(String(b.name || b.email || b.id)));
        setDriverOptions(list);
      } catch (e) {
        console.error("Failed to load drivers", e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!companyId) return setCompanyName("");
    const c = (company ? [company] : allCompanies).find((x) => x.id === companyId);
    setCompanyName(c?.name || "");
  }, [companyId, company, allCompanies]);

  const visibleDrivers = useMemo(() => {
    const q = normalizeText(driverSearch);
    if (!q) return driverOptions;
    return driverOptions.filter((d) => d.search.includes(q));
  }, [driverOptions, driverSearch]);

  const selectedDriverSet = useMemo(() => new Set(selectedDriverIds), [selectedDriverIds]);

  const customerOptions = useMemo(() => {
    const selectedCompanyNorm = normalizeText(companyId);
    return Object.values(customers)
      .filter((c) => {
        if (!selectedCompanyNorm) return true;
        return normalizeText(c.companyId || c.companyID) === selectedCompanyNorm;
      })
      .map((c) => ({
        id: c.id,
        name: c.name || c.customerName || c.id,
        address: addrToText(c.address),
        search: normalizeText(
          [c.name, c.customerName, c.email, c.phone, c.address, c.id]
            .filter(Boolean)
            .join(" ")
        ),
        sites: Array.isArray(c.sites) ? c.sites : [],
      }))
      .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)));
  }, [companyId, customers]);

  const visibleCustomers = useMemo(() => {
    const q = normalizeText(customerSearch);
    if (!q) return customerOptions;
    return customerOptions.filter((c) => c.search.includes(q));
  }, [customerOptions, customerSearch]);

  const selectedCustomerSet = useMemo(
    () => new Set(selectedCustomerIds),
    [selectedCustomerIds]
  );

  const selectedCustomerNameSet = useMemo(() => {
    const selected = selectedCustomerIds.length
      ? customerOptions.filter((c) => selectedCustomerSet.has(c.id))
      : [];
    return new Set(selected.map((c) => normalizeText(c.name)).filter(Boolean));
  }, [customerOptions, selectedCustomerIds.length, selectedCustomerSet]);

  const siteOptions = useMemo(() => {
    const baseCustomers = selectedCustomerIds.length
      ? customerOptions.filter((c) => selectedCustomerSet.has(c.id))
      : customerOptions;

    return baseCustomers
      .flatMap((customer) =>
        customer.sites.map((site) => {
          const label = site.label || site.siteName || site.name || "Unnamed site";
          const address = addrToText(site.address);
          return {
            key: makeSiteSelectionKey(customer.id, site, label),
            id: String(site.id || site.siteId || "").trim(),
            customerId: customer.id,
            customerName: customer.name,
            label,
            address,
            search: normalizeText(
              [customer.name, label, address, site.projectName, site.poNumber, site.siteAccessNotes]
                .filter(Boolean)
                .join(" ")
            ),
          };
        })
      )
      .sort((a, b) => {
        const cmpCustomer = String(a.customerName || "").localeCompare(String(b.customerName || ""));
        if (cmpCustomer !== 0) return cmpCustomer;
        return String(a.label || a.address || "").localeCompare(String(b.label || b.address || ""));
      });
  }, [customerOptions, selectedCustomerIds.length, selectedCustomerSet]);

  const visibleSites = useMemo(() => {
    const q = normalizeText(siteSearch);
    if (!q) return siteOptions;
    return siteOptions.filter((site) => site.search.includes(q));
  }, [siteOptions, siteSearch]);

  const selectedSiteSet = useMemo(() => new Set(selectedSiteKeys), [selectedSiteKeys]);
  const selectedSiteIdSet = useMemo(
    () => new Set(siteOptions.filter((site) => selectedSiteSet.has(site.key)).map((site) => site.id).filter(Boolean)),
    [selectedSiteSet, siteOptions]
  );
  const selectedSiteLabelSet = useMemo(
    () =>
      new Set(
        siteOptions
          .filter((site) => selectedSiteSet.has(site.key))
          .flatMap((site) => [site.label, site.address])
          .map(normalizeText)
          .filter(Boolean)
      ),
    [selectedSiteSet, siteOptions]
  );

  useEffect(() => {
    const validCustomerIds = new Set(customerOptions.map((c) => c.id));
    setSelectedCustomerIds((prev) => prev.filter((id) => validCustomerIds.has(id)));
  }, [customerOptions]);

  useEffect(() => {
    const validSiteKeys = new Set(siteOptions.map((site) => site.key));
    setSelectedSiteKeys((prev) => prev.filter((key) => validSiteKeys.has(key)));
  }, [siteOptions]);

  const toggleDriver = (id) => {
    setSelectedDriverIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleCustomer = (id) => {
    setSelectedCustomerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSite = (key) => {
    setSelectedSiteKeys((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const selectAllDrivers = () => {
    setSelectedDriverIds(driverOptions.map((d) => d.id));
  };

  const clearDriverSelection = () => {
    setSelectedDriverIds([]);
  };

  const selectAllCustomers = () => {
    setSelectedCustomerIds(customerOptions.map((c) => c.id));
  };

  const clearCustomerSelection = () => {
    setSelectedCustomerIds([]);
    setSelectedSiteKeys([]);
  };

  const selectVisibleCustomers = () => {
    setSelectedCustomerIds((prev) => Array.from(new Set([...prev, ...visibleCustomers.map((c) => c.id)])));
  };

  const clearVisibleCustomers = () => {
    const visibleIds = new Set(visibleCustomers.map((c) => c.id));
    setSelectedCustomerIds((prev) => prev.filter((id) => !visibleIds.has(id)));
  };

  const selectAllSites = () => {
    setSelectedSiteKeys(siteOptions.map((site) => site.key));
  };

  const clearSiteSelection = () => {
    setSelectedSiteKeys([]);
  };

  const selectVisibleSites = () => {
    setSelectedSiteKeys((prev) => Array.from(new Set([...prev, ...visibleSites.map((site) => site.key)])));
  };

  const clearVisibleSites = () => {
    const visibleKeys = new Set(visibleSites.map((site) => site.key));
    setSelectedSiteKeys((prev) => prev.filter((key) => !visibleKeys.has(key)));
  };

  const selectVisibleDrivers = () => {
    setSelectedDriverIds((prev) => Array.from(new Set([...prev, ...visibleDrivers.map((d) => d.id)])));
  };

  const clearVisibleDrivers = () => {
    const visibleIds = new Set(visibleDrivers.map((d) => d.id));
    setSelectedDriverIds((prev) => prev.filter((id) => !visibleIds.has(id)));
  };

  const toggleFilterPanel = (key) => {
    setExpandedFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const runReport = async () => {
    if (!companyId) return alert("Choose a company first.");
    if (!fromDate || !toDate) return alert("Pick a date range.");

    setLoading(true);
    setLoanRows([]);
    setLoadRows([]);

    const a = startOfDayMs(fromDate);
    const b = nextDayMs(toDate);

    try {
      const loansSnap = await getDocs(
        query(collection(db, "bin_loans"), where("companyId", "==", companyId))
      );
      const loans = loansSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((x) => {
          const t = tsToMs(x.eventAt) || tsToMs(x.updatedAt);
          return inRange(t, a, b);
        });
      setLoanRows(loans);

      const loadsSnap = await getDocs(
        query(collection(db, "loads"), where("companyId", "==", companyId))
      );
      const loads = loadsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((x) => {
          const t =
            tsToMs(x.serviceDate) || tsToMs(x.createdAt) || tsToMs(x.updatedAt);
          return inRange(t, a, b);
        });
      setLoadRows(loads);
    } catch (e) {
      console.error("runReport failed", e);
      alert("Failed to run report. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  const perBin = useMemo(() => {
    const map = {};

    loanRows.forEach((r) => {
      const key = `${r.categoryId || "uncategorised"}__${r.binId || "unknown"}`;
      const binId = r.binId || "unknown";
      const name = r.binName || "Unknown";
      const categoryName = r.categoryName || "Uncategorised";
      const qty = Number(r.qty || 0);

      if (!map[key]) {
        map[key] = {
          key,
          binId,
          name,
          categoryName,
          delivered: 0,
          pickedUp: 0,
          emptied: 0,
        };
      }

      if (r.direction === "out") map[key].delivered += qty;
      else if (r.direction === "in") map[key].pickedUp += qty;
    });

    loadRows.forEach((l) => {
      const key = `${l.binCategoryId || "uncategorised"}__${l.binId || "unknown"}`;
      const binId = l.binId || "unknown";
      const name = l.binName || "Unknown";
      const categoryName = l.binCategoryName || "Uncategorised";
      const units = Number(l.units || 0);

      if (!map[key]) {
        map[key] = {
          key,
          binId,
          name,
          categoryName,
          delivered: 0,
          pickedUp: 0,
          emptied: 0,
        };
      }

      map[key].emptied += units;
    });

    return Object.values(map).sort((a, b) => {
      const categoryCompare = String(a.categoryName || "").localeCompare(
        String(b.categoryName || "")
      );
      if (categoryCompare !== 0) return categoryCompare;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [loanRows, loadRows]);

  const totals = useMemo(() => {
    const delivered = perBin.reduce((s, r) => s + r.delivered, 0);
    const pickedUp = perBin.reduce((s, r) => s + r.pickedUp, 0);
    const emptied = perBin.reduce((s, r) => s + r.emptied, 0);
    const net = delivered - pickedUp;
    return { delivered, pickedUp, net, emptied };
  }, [perBin]);

  const exportCSV = () => {
    if (!companyId) return;
    const header = [
      "Company",
      "From",
      "To",
      "Category",
      "Bin",
      "Delivered",
      "PickedUp",
      "EmptiedUnits",
      "Net",
    ];
    const lines = perBin.map((r) => [
      companyName || companyId,
      fromDate,
      toDate,
      r.categoryName || "",
      r.name,
      r.delivered,
      r.pickedUp,
      r.emptied,
      r.delivered - r.pickedUp,
    ]);
    const total = [
      companyName || companyId,
      fromDate,
      toDate,
      "TOTAL",
      "",
      totals.delivered,
      totals.pickedUp,
      totals.emptied,
      totals.net,
    ];
    const content = [header, ...lines, total]
      .map((arr) => arr.map(esc).join(","))
      .join("\n");
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    const slug = (companyName || companyId)
      .toLowerCase()
      .replace(/[^\w]+/g, "-");
    a.href = URL.createObjectURL(blob);
    a.download = `company-summary_${slug}_${fromDate}_${toDate}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const exportWasteLoadsCSV = async () => {
    if (!companyId) return;
    if (!loadRows.length) {
      alert("No empty jobs in this range.");
      return;
    }

    const jobIds = Array.from(new Set(loadRows.map((l) => l.jobId).filter(Boolean)));
    const jobsById = {};
    if (jobIds.length) {
      await Promise.all(
        jobIds.map(async (id) => {
          try {
            const s = await getDoc(doc(db, "jobs", id));
            if (s.exists()) jobsById[id] = { id: s.id, ...s.data() };
          } catch {}
        })
      );
    }

    const headers = [
      "Date",
      "Company",
      "DriverEmail",
      "VehicleReg",
      "Category",
      "BinName",
      "Overload",
      "Units (bin)",
      "Material",
      "Percent",
      "UnitsShare",
      "SilicaWeightKg",
      "SilicaBags",
      "ChargeQuantity",
      "ChargeUnit",
      "UnitPrice",
      "Amount",
      "Customer",
      "Site",
      "Notes",
      "PhotoURLs",
    ];

    const totalsByMaterial = {};
    let totalSilicaWeight = 0;
    let totalSilicaBags = 0;
    let totalSilicaAmount = 0;
    const totalsByChargeItem = {};

    const detailRows = [];

    const getSafeCustomer = (row) => {
      const job = jobsById[row.jobId];
      return (
        job?.customerName ||
        row.customerName ||
        customers[row.customerId]?.name ||
        row.customer?.name ||
        ""
      );
    };

    const getSafeSite = (row) => {
      const job = jobsById[row.jobId];
      return (
        addrToText(
          job?.siteLabel || job?.siteName || job?.address || getSafeCustomer(row)
        ) || "-"
      );
    };

    loadRows.forEach((l) => {
      const date = fmtAU(l.serviceDate || l.createdAt || l.updatedAt);
      const companyLabel = companyName || companyId;
      const driver = l.driverEmail || "";
      const rego = l.vehicleReg || l.truckRego || l.registrationNo || "";
      const category = l.binCategoryName || "";
      const bin = l.binName || "";
      const units = Number(l.units ?? 0);
      const overload = l.overload ? "Yes" : "No";
      const noteText = l.notes || l.comment || "";
      const customerLabel = getSafeCustomer(l);
      const siteName = getSafeSite(l);
      const silicaWeightKg = Number(l.silicaWeightKg || 0);
      const silicaBags = Number(l.bagCount || 0);
      const chargeQuantity = Number(l.chargeQuantity || 0);
      const chargeUnit = l.chargeUnitLabel || "";
      const unitPrice = l.unitPrice ?? "";
      const amount = l.amount ?? "";
      const numericAmount = Number(l.amount || 0);

      totalSilicaWeight += silicaWeightKg;
      totalSilicaBags += silicaBags;
      if (
        numericAmount &&
        (Array.isArray(l.materials) ? l.materials : []).some((m) =>
          isSilicaMaterialName(m?.name)
        )
      ) {
        totalSilicaAmount += numericAmount;
      }
      if (l.chargeType && !isSilicaMaterialName(l.chargeType)) {
        if (!totalsByChargeItem[l.chargeType]) {
          totalsByChargeItem[l.chargeType] = {
            quantity: 0,
            unit: chargeUnit,
            amount: 0,
          };
        }
        totalsByChargeItem[l.chargeType].quantity += chargeQuantity;
        totalsByChargeItem[l.chargeType].amount += numericAmount;
      }

      const photos = [];
      if (Array.isArray(l.photos)) {
        photos.push(...l.photos.map((p) => (typeof p === "string" ? p : p?.url || "")));
      }
      if (Array.isArray(l.proofPhotos)) {
        photos.push(
          ...l.proofPhotos.map((p) => (typeof p === "string" ? p : p?.url || ""))
        );
      }
      if (l.loadPhotoUrl) photos.push(l.loadPhotoUrl);

      const photoCell = photos
        .filter(Boolean)
        .map((url, i) => `=HYPERLINK("${url}", "Photo ${i + 1}")`)
        .join(" ; ");

      const mats = Array.isArray(l.materials) ? l.materials : null;

      if (mats && mats.length) {
        mats.forEach((m) => {
          const name = m?.name || "";
          const pct = Number(m?.percent || 0);
          const share = Math.round(units * (pct / 100) * 100) / 100;

          if (name && share) {
            totalsByMaterial[name] = (totalsByMaterial[name] || 0) + share;
          }

          detailRows.push([
            date,
            companyLabel,
            driver,
            rego,
            category,
            bin,
            overload,
            units,
            name,
            pct,
            share,
            silicaWeightKg,
            silicaBags,
            chargeQuantity,
            chargeUnit,
            unitPrice,
            amount,
            customerLabel,
            siteName,
            noteText,
            photoCell,
          ]);
        });
      } else {
        const name = l.wasteType || "";
        const pct = name ? 100 : 0;
        const share = units;

        if (name && share) {
          totalsByMaterial[name] = (totalsByMaterial[name] || 0) + share;
        }

        detailRows.push([
          date,
          companyLabel,
          driver,
          rego,
          category,
          bin,
          overload,
          units,
          name,
          pct,
          share,
          silicaWeightKg,
          silicaBags,
          chargeQuantity,
          chargeUnit,
          unitPrice,
          amount,
          customerLabel,
          siteName,
          noteText,
          photoCell,
        ]);
      }
    });

    const totalsHeader = ["Material", "UnitsTotal"];
    const totalsRows = Object.entries(totalsByMaterial)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, u]) => [name, Math.round(u * 100) / 100]);

    const csv = [
      headers,
      ...detailRows,
      [],
      ["Totals by Material (units)"],
      totalsHeader,
      ...totalsRows,
      [],
      ["Silica Summary"],
      ["Total Silica Weight (kg)", Math.round(totalSilicaWeight * 100) / 100],
      ["Total Silica Bags", totalSilicaBags],
      ["Total Silica Amount", Math.round(totalSilicaAmount * 100) / 100],
      [],
      ["Charge Item Summary"],
      ["Item", "Quantity", "Unit", "Amount"],
      ...Object.entries(totalsByChargeItem)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([name, total]) => [
          name,
          total.quantity,
          total.unit,
          Math.round(total.amount * 100) / 100,
        ]),
    ]
      .map((arr) => arr.map(esc).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    const slug = (companyName || companyId)
      .toLowerCase()
      .replace(/[^\w]+/g, "-");
    a.href = URL.createObjectURL(blob);
    a.download = `empties_detail_${slug}_${fromDate}_${toDate}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const exportAdvancedCSV = async () => {
    if (!companyId) return alert("Choose a company first.");
    if (!fromDate || !toDate) return alert("Pick a date range.");
    if (!driverOptions.length) return alert("No driver accounts found.");
    if (!selectedExportColumns.length) {
      return alert("Choose at least one column for advanced export.");
    }

    setExportingAdvanced(true);
    try {
      const activeDriverIds = selectedDriverIds.length
        ? selectedDriverIds
        : driverOptions.map((d) => d.id);

      const dateKeys = getDateRangeArray(fromDate, toDate);
      const driverMap = Object.fromEntries(driverOptions.map((d) => [d.id, d]));
      const companyLabel = companyName || companyId;
      const selectedCompanyNorm = normalizeText(companyId);
      const selectedCompanyNameNorm = normalizeText(companyName);

      const assignmentEntries = [];
      await Promise.all(
        activeDriverIds.flatMap((driverId) =>
          dateKeys.map(async (dateKey) => {
            try {
              const scopedId = `${companyId}_${driverId}_${dateKey}`;
              const legacyId = `${driverId}_${dateKey}`;
              let snap = await getDoc(doc(db, "assignments", scopedId));
              if (!snap.exists()) {
                snap = await getDoc(doc(db, "assignments", legacyId));
              }
              if (snap.exists()) {
                assignmentEntries.push({
                  driverId,
                  date: dateKey,
                  data: snap.data(),
                });
              }
            } catch (err) {
              console.warn("Failed to fetch assignment", driverId, dateKey, err);
            }
          })
        )
      );

      const flatStops = [];
      assignmentEntries.forEach(({ driverId, date, data }) => {
        Object.entries(data?.runs || {}).forEach(([runKey, list]) => {
          (Array.isArray(list) ? list : []).forEach((item, index) => {
            if (!item || item.isWeighbridgeDocket) return;
            flatStops.push({
              driverId,
              date,
              runKey,
              stopIndex: index,
              assignment: data,
              item,
            });
          });
        });
      });

      if (!flatStops.length) {
        alert("No jobs found for the selected export range.");
        return;
      }

      const jobIds = Array.from(
        new Set(flatStops.map((s) => s.item?.jobId).filter(Boolean))
      );
      const jobsById = {};
      if (jobIds.length) {
        await Promise.all(
          jobIds.map(async (jobId) => {
            try {
              const snap = await getDoc(doc(db, "jobs", jobId));
              if (snap.exists()) jobsById[jobId] = { id: snap.id, ...snap.data() };
            } catch (e) {
              console.warn("Failed to load job", jobId, e);
            }
          })
        );
      }

      const servicesSnap = await getDocs(collection(db, "jobTypes"));
      const allServices = servicesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const companyServices = allServices.filter((s) => {
        const svcCompanyId = normalizeText(s.companyId || s.companyID || "");
        const svcCompanyName = normalizeText(s.companyName || s.company || "");
        if (svcCompanyId && svcCompanyId === selectedCompanyNorm) return true;
        if (!svcCompanyId && svcCompanyName && svcCompanyName === selectedCompanyNameNorm)
          return true;
        if (!svcCompanyId && !svcCompanyName) return true;
        return false;
      });

      const servicesById = {};
      const servicesByCode = {};
      const servicesByName = {};
      companyServices.forEach((s) => {
        servicesById[s.id] = s;
        [s.code, s.serviceCode, s.jobTypeCode, s.slug].forEach((code) => {
          const key = slugify(code);
          if (key) servicesByCode[key] = s;
        });
        [s.name, s.label, s.serviceName, s.title].forEach((name) => {
          const key = slugify(name);
          if (key) servicesByName[key] = s;
        });
      });

      const findServiceByMeaning = (value) => {
        const key = slugify(value);
        if (!key) return null;
        return (
          servicesByCode[key] ||
          servicesByName[key] ||
          companyServices.find((service) => {
            const search = [
              service.code,
              service.serviceCode,
              service.jobTypeCode,
              service.slug,
              service.name,
              service.label,
              service.serviceName,
              service.title,
            ]
              .map(slugify)
              .filter(Boolean)
              .join(" ");

            if (key.includes("empty") || key.includes("waste") || key === "emp") {
              return search.includes("empty") || search.includes("waste") || search.includes("emp");
            }
            if (key.includes("deliver") || key === "dv") {
              return search.includes("deliver") || search.includes("drop") || search.includes("dv");
            }
            if (key.includes("pickup") || key.includes("pick-up") || key === "pk") {
              return search.includes("pickup") || search.includes("pick") || search.includes("pk");
            }
            return search.includes(key);
          }) ||
          null
        );
      };

      let companyLoads = loadRows;
      if (!companyLoads.length) {
        const a = startOfDayMs(fromDate);
        const b = nextDayMs(toDate);
        try {
          const loadsSnap = await getDocs(
            query(collection(db, "loads"), where("companyId", "==", companyId))
          );
          companyLoads = loadsSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((x) => {
              const t = tsToMs(x.serviceDate) || tsToMs(x.createdAt) || tsToMs(x.updatedAt);
              return inRange(t, a, b);
            });
        } catch (e) {
          console.warn("Failed to fetch loads for advanced export", e);
          companyLoads = [];
        }
      }

      const loadsByJobId = companyLoads.reduce((acc, row) => {
        if (!row?.jobId) return acc;
        if (!acc[row.jobId]) acc[row.jobId] = [];
        acc[row.jobId].push(row);
        return acc;
      }, {});

      const companyMatches = (item, job) => {
        const ids = [
          item?.companyId,
          item?.companyID,
          job?.companyId,
          job?.companyID,
        ]
          .map(normalizeText)
          .filter(Boolean);
        if (ids.includes(selectedCompanyNorm)) return true;

        const names = [
          item?.companyName,
          item?.company,
          job?.companyName,
          job?.company,
          item?.customerCompany,
        ]
          .map(normalizeText)
          .filter(Boolean);
        if (selectedCompanyNameNorm && names.includes(selectedCompanyNameNorm)) return true;
        return false;
      };

      const resolveServiceRecord = (item, job) => {
        const ids = [item?.serviceId, item?.jobTypeId, job?.serviceId, job?.jobTypeId]
          .map((v) => String(v || "").trim())
          .filter(Boolean);
        for (const id of ids) {
          if (servicesById[id]) return servicesById[id];
        }

        const codes = [
          item?.serviceCode,
          item?.jobTypeCode,
          item?.code,
          job?.serviceCode,
          job?.jobTypeCode,
          job?.code,
          item?.service,
          job?.service,
        ]
          .map(slugify)
          .filter(Boolean);
        for (const code of codes) {
          if (servicesByCode[code]) return servicesByCode[code];
        }

        const names = [
          item?.serviceName,
          item?.jobTypeName,
          item?.service,
          item?.jobType,
          job?.serviceName,
          job?.jobTypeName,
          job?.service,
          job?.jobType,
          job?.type,
          item?.type,
        ]
          .map(slugify)
          .filter(Boolean);
        for (const name of names) {
          if (servicesByName[name]) return servicesByName[name];
        }
        return (
          findServiceByMeaning(
            firstDefined(
              item?.serviceName,
              item?.jobTypeName,
              item?.service,
              item?.jobType,
              job?.serviceName,
              job?.jobTypeName,
              job?.service,
              job?.jobType,
              job?.type,
              item?.type
            )
          ) || null
        );
      };

      const resolveQty = (item, job, jobLoads) => {
        const explicit = [
          item?.serviceBinQty,
          item?.billableBinQty,
          item?.jobTypeBinQty,
          item?.qtyBinsCollected,
          item?.quantity,
          item?.binQty,
          job?.serviceBinQty,
          job?.billableBinQty,
          job?.jobTypeBinQty,
          job?.qtyBinsCollected,
          job?.binQty,
          job?.quantity,
          job?.emptyLoadTotals?.totalUnits,
          job?.totalEmptied,
          job?.totalDelivered,
          job?.totalPickedUp,
        ].map(toNumber);
        const explicitFound = explicit.find((n) => n > 0);
        if (explicitFound) return explicitFound;

        const deliveredTotal = sumQtyArray(job?.deliveries);
        if (deliveredTotal > 0) return deliveredTotal;
        const pickedUpTotal = sumQtyArray(job?.pickups);
        if (pickedUpTotal > 0) return pickedUpTotal;
        const emptyLoadsFromJob = sumQtyArray(job?.emptyLoads);
        if (emptyLoadsFromJob > 0) return emptyLoadsFromJob;

        const loadsQty = (Array.isArray(jobLoads) ? jobLoads : []).reduce(
          (sum, row) => sum + toNumber(row?.units),
          0
        );
        if (loadsQty > 0) return loadsQty;

        const parsed = parseQtyFromText(
          [item?.description, item?.driverNotes, job?.driverNotes, job?.description]
            .filter(Boolean)
            .join(" | ")
        );
        return parsed || 0;
      };

      const resolveComponentServiceRecord = (component, fallbackCode, fallbackName) => {
        const ids = [component?.serviceId, component?.jobTypeId]
          .map((v) => String(v || "").trim())
          .filter(Boolean);
        for (const id of ids) {
          if (servicesById[id]) return servicesById[id];
        }

        const codes = [component?.serviceCode, component?.jobTypeCode, fallbackCode]
          .map(slugify)
          .filter(Boolean);
        for (const code of codes) {
          if (servicesByCode[code]) return servicesByCode[code];
        }

        const names = [
          component?.serviceName,
          component?.jobTypeName,
          component?.service,
          component?.jobType,
          component?.type,
          fallbackName,
        ]
          .map(slugify)
          .filter(Boolean);
        for (const name of names) {
          if (servicesByName[name]) return servicesByName[name];
        }
        return findServiceByMeaning(fallbackName || fallbackCode) || null;
      };

      const buildComponentRows = (
        stop,
        item,
        job,
        baseMeta,
        jobLoads,
        wasteExportFields = {}
      ) => {
        const rawRows = [];
        const stopFallbackMs = tsToMs(firstDefined(job?.updatedAt, item?.updatedAt, stop.date));
        const parentServiceRecord = resolveServiceRecord(item, job);

        const pushComponentRow = (component, fallbackCode, fallbackName, fallbackOrder) => {
          const componentRecord = resolveComponentServiceRecord(
            component,
            fallbackCode,
            fallbackName
          );

          const explicitServiceName = firstDefined(
            component?.serviceName,
            component?.jobTypeName,
            component?.service,
            component?.jobType,
            component?.type
          );

          const serviceName = canonicalServiceLabel(
            firstDefined(componentRecord?.name, componentRecord?.label, explicitServiceName),
            fallbackName
          );

          const qty = getQty(component);
          const rawUnitAmount = firstDefined(
            component?.servicePrice,
            component?.unitAmount,
            component?.price,
            component?.rate,
            component?.pricePerBin,
            componentRecord?.price,
            componentRecord?.unitAmount,
            componentRecord?.pricePerBin,
            componentRecord?.servicePrice,
            componentRecord?.rate,
            parentServiceRecord?.price,
            parentServiceRecord?.unitAmount,
            parentServiceRecord?.pricePerBin,
            parentServiceRecord?.servicePrice,
            parentServiceRecord?.rate,
            job?.servicePrice,
            job?.unitAmount,
            job?.price,
            job?.rate
          );
          const unitAmount = isTruthyPrice(rawUnitAmount) ? toNumber(rawUnitAmount) : "";
          const lineTotal =
            isTruthyPrice(unitAmount) && qty > 0
              ? Math.round(qty * unitAmount * 100) / 100
              : "";
          const description =
            firstDefined(component?.description, component?.notes, baseMeta.description) || "";
          const driverNotes =
            firstDefined(component?.driverNotes, component?.notes, baseMeta.driverNotes) || "";

          const hasRowData =
            qty > 0 ||
            isTruthyPrice(unitAmount) ||
            Boolean(serviceName) ||
            Boolean(description) ||
            Boolean(driverNotes);

          if (!hasRowData) return;

          rawRows.push({
            ...baseMeta,
            service: serviceName,
            qtyBinsCollected: qty || "",
            unitAmount: isTruthyPrice(unitAmount) ? fmtCurrency(unitAmount) : "",
            lineTotal: lineTotal !== "" ? fmtCurrency(lineTotal) : "",
            description,
            driverNotes,
            __sortMs: componentSortMs(component, stopFallbackMs),
            __fallbackOrder: fallbackOrder,
          });
        };

        (Array.isArray(job?.deliveries) ? job.deliveries : []).forEach((entry, index) => {
          pushComponentRow(entry, "DELIVER", "DELIVER", 1000 + index);
        });

        (Array.isArray(job?.pickups) ? job.pickups : []).forEach((entry, index) => {
          pushComponentRow(entry, "PICKUP", "Pick Up", 3000 + index);
        });

        const emptyComponents = Array.isArray(job?.emptyLoads) && job.emptyLoads.length
          ? job.emptyLoads
          : (Array.isArray(jobLoads) ? jobLoads : []).map((load) => ({
              ...load,
              qty: firstDefined(load?.qty, load?.units, load?.quantity),
              unitAmount: firstDefined(load?.unitAmount, load?.price, load?.rate),
              description: firstDefined(load?.description, load?.material, load?.wasteType),
            }));

        emptyComponents.forEach((entry, index) => {
          pushComponentRow(entry, "EMPTY", "EMPTY", 2000 + index);
        });

        rawRows.sort((a, b) => {
          if (a.__sortMs !== b.__sortMs) return a.__sortMs - b.__sortMs;
          return a.__fallbackOrder - b.__fallbackOrder;
        });

        const mergedRows = [];
        rawRows.forEach((row) => {
          const prev = mergedRows[mergedRows.length - 1];
          const sameAsPrev =
            prev &&
            normalizeText(prev.service) === normalizeText(row.service) &&
            normalizeText(prev.unitAmount) === normalizeText(row.unitAmount) &&
            normalizeText(prev.description) === normalizeText(row.description) &&
            normalizeText(prev.driverNotes) === normalizeText(row.driverNotes);

          if (sameAsPrev) {
            const prevQty = toNumber(prev.qtyBinsCollected);
            const rowQty = toNumber(row.qtyBinsCollected);
            const mergedQty = prevQty + rowQty;
            prev.qtyBinsCollected = mergedQty || "";

            const prevTotal = toNumber(prev.lineTotal);
            const rowTotal = toNumber(row.lineTotal);
            const mergedTotal = prevTotal + rowTotal;
            prev.lineTotal = mergedTotal ? fmtCurrency(mergedTotal) : "";
            return;
          }

          mergedRows.push({ ...row });
        });

        const firstEmptyRow = mergedRows.find((row) =>
          normalizeText(row.service).includes("empty")
        );
        if (firstEmptyRow) {
          Object.assign(firstEmptyRow, wasteExportFields);
        }

        return mergedRows.map(({ __sortMs, __fallbackOrder, ...row }) => row);
      };

      const rows = [];
      flatStops.forEach((stop) => {
        const item = stop.item || {};
        const job = jobsById[item.jobId] || {};
        if (!companyMatches(item, job)) return;

        const address = addrToText(
          firstDefined(item?.address, job?.address, item?.siteAddress, job?.siteAddress)
        );
        const site =
          addrToText(
            firstDefined(
              item?.siteLabel,
              item?.siteName,
              job?.siteLabel,
              job?.siteName,
              item?.site,
              job?.site,
              address
            )
          ) || "";
        const customer =
          firstDefined(
            job?.customerName,
            item?.customerName,
            customers[job?.customerId || item?.customerId]?.name,
            companyLabel
          ) || "";
        const customerId = String(job?.customerId || item?.customerId || "").trim();
        const customerNameNorm = normalizeText(customer);
        const siteId = String(job?.siteId || item?.siteId || "").trim();
        const jobKey =
          item.jobId ||
          job.id ||
          `${stop.driverId}_${stop.date}_${stop.runKey}_${stop.stopIndex}`;
        const driver = driverMap[stop.driverId]?.name || driverMap[stop.driverId]?.email || stop.driverId;
        const driverEmail = driverMap[stop.driverId]?.email || item?.driverEmail || job?.driverEmail || "";
        const vehicleReg =
          firstDefined(
            item?.vehicleReg,
            item?.truckRego,
            item?.rego,
            job?.vehicleReg,
            job?.truckRego,
            stop.assignment?.runMeta?.[stop.runKey]?.vehicleRego,
            stop.assignment?.runMeta?.[stop.runKey]?.rego
          ) || "";
        const poNumber =
          firstDefined(item?.poNumber, job?.poNumber, job?.site?.poNumber, item?.site?.poNumber) || "";
        const dateLabel = fmtDateAU(firstDefined(job?.date, item?.date, stop.date));
        const jobLoads = loadsByJobId[item.jobId] || [];
        const wasteSummary = summarizeWasteCharges(job, jobLoads);
        const wasteChargesTotal =
          wasteSummary.chairsAmount +
          wasteSummary.palletsAmount;
        const wasteExportFields = {
          silicaWeightKg: wasteSummary.silicaWeightKg
            ? Math.round(wasteSummary.silicaWeightKg * 100) / 100
            : "",
          silicaBags: wasteSummary.silicaBags || "",
          silicaAmount: wasteSummary.silicaAmount
            ? fmtCurrency(wasteSummary.silicaAmount)
            : "",
          chairsQty: wasteSummary.chairsQty || "",
          chairsAmount: wasteSummary.chairsAmount
            ? fmtCurrency(wasteSummary.chairsAmount)
            : "",
          palletsQty: wasteSummary.palletsQty || "",
          palletsAmount: wasteSummary.palletsAmount
            ? fmtCurrency(wasteSummary.palletsAmount)
            : "",
          wasteChargesTotal: wasteChargesTotal ? fmtCurrency(wasteChargesTotal) : "",
        };
        const isFutileStop =
          Boolean(item?.futile || job?.futile) ||
          String(firstDefined(item?.status, job?.status, "")).toLowerCase() === "futile" ||
          String(firstDefined(item?.skipReason, job?.skipReason, "")).toLowerCase() === "futile";
        const futileAmount = isFutileStop
          ? toNumber(
              firstDefined(
                item?.futileCharge,
                item?.futileAmount,
                job?.futileCharge,
                job?.futileAmount,
                FUTILE_CHARGE_AMOUNT
              )
            ) || FUTILE_CHARGE_AMOUNT
          : 0;
        const futileNote =
          firstDefined(item?.futileNote, job?.futileNote, item?.skipNote, job?.skipNote) || "";

        const baseMeta = {
          company: companyLabel,
          customer,
          date: dateLabel,
          address,
          site,
          driver,
          driverEmail,
          vehicleReg,
          description: firstDefined(item?.description, job?.description) || "",
          driverNotes: firstDefined(job?.driverNotes, item?.driverNotes) || "",
          futileCharge: "",
          futileNote: "",
          poNumber,
          __customerId: customerId,
          __customerNameNorm: customerNameNorm,
          __jobKey: jobKey,
          __siteId: siteId,
          __siteKey: makeSiteSelectionKey(customerId, { id: siteId, label: site }, site),
          __siteNorm: normalizeText(site || address),
          __addressNorm: normalizeText(address),
        };

        const futileRow = isFutileStop
          ? {
              ...baseMeta,
              service: "FUTILE",
              qtyBinsCollected: 1,
              unitAmount: fmtCurrency(futileAmount),
              lineTotal: fmtCurrency(futileAmount),
              description: "Futile charge",
              driverNotes: futileNote || baseMeta.driverNotes,
              futileCharge: fmtCurrency(futileAmount),
              futileNote,
            }
          : null;

        const componentRows = buildComponentRows(
          stop,
          item,
          job,
          baseMeta,
          jobLoads,
          wasteExportFields
        );

        if (componentRows.length) {
          rows.push(...componentRows);
          if (futileRow) rows.push(futileRow);
          return;
        }

        const serviceRecord = resolveServiceRecord(item, job);
        const serviceName =
          firstDefined(
            serviceRecord?.name,
            serviceRecord?.label,
            job?.serviceName,
            job?.jobTypeName,
            job?.service,
            job?.jobType,
            item?.serviceName,
            item?.jobTypeName,
            item?.service,
            item?.jobType,
            item?.type,
            job?.type
          ) || "";

        const qty = resolveQty(item, job, jobLoads);
        const rawUnitAmount = firstDefined(
          item?.servicePrice,
          item?.unitAmount,
          item?.pricePerBin,
          item?.price,
          item?.rate,
          job?.servicePrice,
          job?.unitAmount,
          job?.pricePerBin,
          job?.price,
          job?.rate,
          serviceRecord?.price,
          serviceRecord?.unitAmount,
          serviceRecord?.pricePerBin,
          serviceRecord?.servicePrice,
          serviceRecord?.rate
        );
        const unitAmount = isTruthyPrice(rawUnitAmount) ? toNumber(rawUnitAmount) : "";
        const lineTotal = isTruthyPrice(unitAmount) ? Math.round(qty * unitAmount * 100) / 100 : "";

        rows.push({
          ...baseMeta,
          ...(normalizeText(serviceName).includes("empty")
            ? wasteExportFields
            : {}),
          service: serviceName,
          qtyBinsCollected: qty || "",
          unitAmount: isTruthyPrice(unitAmount) ? fmtCurrency(unitAmount) : "",
          lineTotal: lineTotal !== "" ? fmtCurrency(lineTotal) : "",
        });
        if (futileRow) rows.push(futileRow);
      });

      const filteredRows = rows.filter((row) => {
        const matchesCustomer =
          !selectedCustomerIds.length ||
          selectedCustomerSet.has(row.__customerId) ||
          selectedCustomerNameSet.has(row.__customerNameNorm);

        const matchesSite =
          !selectedSiteKeys.length ||
          selectedSiteSet.has(row.__siteKey) ||
          (row.__siteId && selectedSiteIdSet.has(row.__siteId)) ||
          selectedSiteLabelSet.has(row.__siteNorm) ||
          selectedSiteLabelSet.has(row.__addressNorm);

        return matchesCustomer && matchesSite;
      });

      if (!filteredRows.length) {
        alert("No jobs found for the selected export range.");
        return;
      }

      const sortValue = (v) => String(v || "").toLowerCase();
      filteredRows.sort((a, b) => {
        const cmpCustomer = sortValue(a.customer).localeCompare(sortValue(b.customer));
        if (cmpCustomer !== 0) return cmpCustomer;
        const cmpSite = sortValue(a.site).localeCompare(sortValue(b.site));
        if (cmpSite !== 0) return cmpSite;
        const cmpDate = sortValue(a.date).localeCompare(sortValue(b.date));
        if (cmpDate !== 0) return cmpDate;
        return sortValue(a.address).localeCompare(sortValue(b.address));
      });

      const headerMap = {
        company: "Company",
        customer: "Customer",
        date: "Date",
        driver: "Driver",
        driverEmail: "DriverEmail",
        address: "Address",
        description: "Description",
        site: "Site",
        service: "Service",
        qtyBinsCollected: "QtyBinsCollected",
        unitAmount: "UnitAmount",
        lineTotal: "LineTotal",
        silicaWeightKg: "SilicaWeightKg",
        silicaBags: "SilicaBags",
        silicaAmount: "SilicaAmount",
        chairsQty: "ChairsQty",
        chairsAmount: "ChairsAmount",
        palletsQty: "PalletsQty",
        palletsAmount: "PalletsAmount",
        wasteChargesTotal: "WasteChargesTotal",
        futileCharge: "FutileCharge",
        futileNote: "FutileNote",
        totalAmount: "TotalAmount",
        vehicleReg: "VehicleReg",
        driverNotes: "DriverNotes",
        poNumber: "PONumber",
      };

      const totalAmountColumn = "totalAmount";
      const outputColumns = [
        ...selectedExportColumns.filter((key) => key !== totalAmountColumn),
        totalAmountColumn,
      ];

      const totalsByJob = {};
      const lastRowIndexByJob = {};
      filteredRows.forEach((row, index) => {
        const key = row.__jobKey || `row_${index}`;
        if (!totalsByJob[key]) totalsByJob[key] = 0;
        totalsByJob[key] +=
          toNumber(row.lineTotal) +
          toNumber(row.silicaAmount) +
          toNumber(row.wasteChargesTotal);
        lastRowIndexByJob[key] = index;
      });

      const outputRows = filteredRows.map((row, index) => {
        const key = row.__jobKey || `row_${index}`;
        const isLastJobRow = lastRowIndexByJob[key] === index;
        return {
          ...row,
          totalAmount: isLastJobRow ? fmtCurrency(totalsByJob[key]) : "",
        };
      });

      const csvLines = [];
      csvLines.push(outputColumns.map((key) => headerMap[key] || key));

      let lastGroupKey = "";
      outputRows.forEach((row) => {
        if (groupAdvancedBySite) {
          const groupKey = `${row.customer}__${row.site || row.address}`;
          if (groupKey !== lastGroupKey) {
            lastGroupKey = groupKey;
            const sep = new Array(outputColumns.length).fill("");
            sep[0] = `Site group: ${row.customer} - ${row.site || row.address}`;
            csvLines.push(sep);
          }
        }
        csvLines.push(outputColumns.map((key) => row[key] ?? ""));
      });

      const csv = "\uFEFF" + csvLines.map((line) => line.map(esc).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `company-summary-advanced_${slugify(companyLabel)}_${fromDate}_${toDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error("Advanced export failed", e);
      alert("Failed to export advanced CSV. See console for details.");
    } finally {
      setExportingAdvanced(false);
    }
  };

  const hasCompanyProp = !!company;

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 space-y-6 text-gray-900">
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="text-sm font-semibold text-gray-900">
          {companyName || companyId || "No company selected"}
        </div>
        <div className="mt-1 text-xs text-gray-600">
          <span className="font-semibold text-gray-900">{totals.delivered}</span> delivered
          {" · "}
          <span className="font-semibold text-gray-900">{totals.pickedUp}</span> picked up
          {" · "}
          <span className="font-semibold text-gray-900">{totals.emptied}</span> emptied
        </div>
      </div>

      <div className="rounded-[24px] border border-gray-200 bg-white p-4 shadow-sm grid grid-cols-1 md:grid-cols-4 gap-4">
        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
            Company
          </span>
          <select
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
            disabled={hasCompanyProp}
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
          >
            {!hasCompanyProp && <option value="">Select company</option>}
            {(hasCompanyProp ? [company] : allCompanies).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || c.id}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
            From
          </span>
          <input
            type="date"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </label>

        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
            To
          </span>
          <input
            type="date"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </label>

        <div className="flex items-end">
          <button
            onClick={runReport}
            disabled={!companyId || !fromDate || !toDate || loading}
            className={`w-full rounded-lg px-4 py-2 text-sm font-medium text-white transition ${
              !companyId || !fromDate || !toDate || loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            {loading ? "Loading..." : "Run report"}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => toggleFilterPanel("drivers")}
            className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
          >
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">Driver filter</h3>
              <p className="truncate text-sm text-gray-600">
                {selectedDriverIds.length ? `${selectedDriverIds.length} selected` : "All drivers"}
              </p>
            </div>
            <span className="shrink-0 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700">
              {expandedFilters.drivers ? "Hide" : "Edit"}
            </span>
          </button>
        </div>

        {expandedFilters.drivers && (
          <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
            <div className="flex flex-wrap gap-2">
              <button onClick={selectAllDrivers} type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">Select all</button>
              <button onClick={clearDriverSelection} type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">All drivers</button>
              <button onClick={selectVisibleDrivers} type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">Select visible</button>
              <button onClick={clearVisibleDrivers} type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">Clear visible</button>
            </div>

            <label className="block">
              <span className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">Search drivers</span>
              <input
                type="text"
                value={driverSearch}
                onChange={(e) => setDriverSearch(e.target.value)}
                placeholder="Search by name, email, or driver ID"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
              />
            </label>

            <div className="text-sm text-gray-600">
              Showing {visibleDrivers.length} of {driverOptions.length} drivers. {selectedDriverIds.length ? `${selectedDriverIds.length} selected.` : "All drivers selected for export."}
            </div>

            <div className="grid max-h-72 gap-2 overflow-y-auto pr-1 md:grid-cols-2 xl:grid-cols-3">
              {visibleDrivers.map((driver) => (
                <label key={driver.id} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2 transition ${selectedDriverSet.has(driver.id) ? "border-emerald-300 bg-emerald-50" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    checked={selectedDriverSet.has(driver.id)}
                    onChange={() => toggleDriver(driver.id)}
                  />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-gray-900">{driver.name || driver.email || driver.id}</div>
                    <div className="truncate text-xs text-gray-500">{driver.email || driver.id}</div>
                  </div>
                </label>
              ))}
              {!visibleDrivers.length && (
                <div className="rounded-xl border border-dashed border-gray-300 px-4 py-5 text-sm text-gray-500">No matching drivers found.</div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => toggleFilterPanel("customers")}
            className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
          >
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">Customer filter</h3>
              <p className="truncate text-sm text-gray-600">
                {selectedCustomerIds.length ? `${selectedCustomerIds.length} selected` : "All customers"}
              </p>
            </div>
            <span className="shrink-0 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700">
              {expandedFilters.customers ? "Hide" : "Edit"}
            </span>
          </button>
        </div>

        {expandedFilters.customers && (
          <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
            <div className="flex flex-wrap gap-2">
              <button onClick={selectAllCustomers} type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">Select all</button>
              <button onClick={clearCustomerSelection} type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">All customers</button>
              <button onClick={selectVisibleCustomers} type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">Select visible</button>
              <button onClick={clearVisibleCustomers} type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">Clear visible</button>
            </div>

            <label className="block">
              <span className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">Search customers</span>
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Search by customer, address, email, or phone"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
              />
            </label>

            <div className="text-sm text-gray-600">
              Showing {visibleCustomers.length} of {customerOptions.length} customers. {selectedCustomerIds.length ? `${selectedCustomerIds.length} selected.` : "All customers selected for export."}
            </div>

            <div className="grid max-h-72 gap-2 overflow-y-auto pr-1 md:grid-cols-2 xl:grid-cols-3">
              {visibleCustomers.map((customer) => (
                <label key={customer.id} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2 transition ${selectedCustomerSet.has(customer.id) ? "border-emerald-300 bg-emerald-50" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    checked={selectedCustomerSet.has(customer.id)}
                    onChange={() => toggleCustomer(customer.id)}
                  />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-gray-900">{customer.name || customer.id}</div>
                    <div className="truncate text-xs text-gray-500">{customer.address || `${customer.sites.length} site${customer.sites.length === 1 ? "" : "s"}`}</div>
                  </div>
                </label>
              ))}
              {!visibleCustomers.length && (
                <div className="rounded-xl border border-dashed border-gray-300 px-4 py-5 text-sm text-gray-500">No matching customers found.</div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => toggleFilterPanel("sites")}
            className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
          >
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">Site filter</h3>
              <p className="truncate text-sm text-gray-600">
                {selectedSiteKeys.length ? `${selectedSiteKeys.length} selected` : "All sites"}
              </p>
            </div>
            <span className="shrink-0 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700">
              {expandedFilters.sites ? "Hide" : "Edit"}
            </span>
          </button>
        </div>

        {expandedFilters.sites && (
          <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
            <div className="flex flex-wrap gap-2">
              <button onClick={selectAllSites} type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">Select all</button>
              <button onClick={clearSiteSelection} type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">All sites</button>
              <button onClick={selectVisibleSites} type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">Select visible</button>
              <button onClick={clearVisibleSites} type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">Clear visible</button>
            </div>

            <label className="block">
              <span className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">Search sites</span>
              <input
                type="text"
                value={siteSearch}
                onChange={(e) => setSiteSearch(e.target.value)}
                placeholder="Search by customer, site, address, project, or PO"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
              />
            </label>

            <div className="text-sm text-gray-600">
              Showing {visibleSites.length} of {siteOptions.length} sites. {selectedSiteKeys.length ? `${selectedSiteKeys.length} selected.` : "All sites selected for export."}
            </div>

            <div className="grid max-h-72 gap-2 overflow-y-auto pr-1 md:grid-cols-2 xl:grid-cols-3">
              {visibleSites.map((site) => (
                <label key={site.key} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2 transition ${selectedSiteSet.has(site.key) ? "border-emerald-300 bg-emerald-50" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    checked={selectedSiteSet.has(site.key)}
                    onChange={() => toggleSite(site.key)}
                  />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-gray-900">{site.label || site.address || "Unnamed site"}</div>
                    <div className="truncate text-xs text-gray-500">{site.customerName}{site.address ? ` - ${site.address}` : ""}</div>
                  </div>
                </label>
              ))}
              {!visibleSites.length && (
                <div className="rounded-xl border border-dashed border-gray-300 px-4 py-5 text-sm text-gray-500">No matching sites found.</div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <button
            type="button"
            onClick={() => toggleFilterPanel("export")}
            className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
          >
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">Advanced CSV export</h3>
              <p className="truncate text-sm text-gray-600">
                {selectedExportColumns.length} columns selected
              </p>
            </div>
            <span className="shrink-0 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700">
              {expandedFilters.export ? "Hide" : "Edit columns"}
            </span>
          </button>
          <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              checked={groupAdvancedBySite}
              onChange={(e) => setGroupAdvancedBySite(e.target.checked)}
            />
            Group by site
          </label>
        </div>

        {expandedFilters.export && (
          <div className="mt-3 grid max-h-72 gap-2 overflow-y-auto border-t border-gray-100 pt-3 pr-1 md:grid-cols-2 xl:grid-cols-3">
            {ADVANCED_EXPORT_OPTIONS.map((opt) => {
              const selected = selectedExportColumns.includes(opt.key);
              return (
                <label key={opt.key} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2 transition ${selected ? "border-emerald-300 bg-emerald-50" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    checked={selected}
                    onChange={() => {
                      setSelectedExportColumns((prev) =>
                        prev.includes(opt.key)
                          ? prev.filter((k) => k !== opt.key)
                          : [...prev, opt.key]
                      );
                    }}
                  />
                  <span className="text-sm font-medium text-gray-900">{opt.label}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={exportCSV}
          disabled={!perBin.length}
          className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition ${
            perBin.length
              ? "bg-gray-900 hover:bg-black"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          Export summary CSV
        </button>

        <button
          onClick={exportWasteLoadsCSV}
          disabled={!loadRows.length}
          className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition ${
            loadRows.length
              ? "bg-emerald-600 hover:bg-emerald-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          Export waste loads CSV
        </button>

        <button
          onClick={exportAdvancedCSV}
          disabled={!companyId || !fromDate || !toDate || exportingAdvanced}
          className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition ${
            !companyId || !fromDate || !toDate || exportingAdvanced
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {exportingAdvanced ? "Exporting advanced CSV..." : "Export advanced CSV"}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-600">Loading report...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Delivered
              </div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">
                {totals.delivered}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Picked up
              </div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">
                {totals.pickedUp}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Emptied (units)
              </div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">
                {totals.emptied}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Net
              </div>
              <div
                className={`mt-1 text-2xl font-semibold tabular-nums ${
                  totals.net < 0 ? "text-red-600" : ""
                }`}
              >
                {totals.net}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white overflow-x-auto shadow-sm">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Category
                  </th>
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Bin
                  </th>
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Delivered
                  </th>
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Picked up
                  </th>
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Emptied (units)
                  </th>
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Net
                  </th>
                </tr>
              </thead>
              <tbody>
                {perBin.map((r) => (
                  <tr key={r.key} className="border-t border-gray-100">
                    <td className="p-3">{r.categoryName || "-"}</td>
                    <td className="p-3">{r.name}</td>
                    <td className="p-3 tabular-nums">{r.delivered}</td>
                    <td className="p-3 tabular-nums">{r.pickedUp}</td>
                    <td className="p-3 tabular-nums">{r.emptied}</td>
                    <td
                      className={`p-3 tabular-nums ${
                        r.delivered - r.pickedUp < 0
                          ? "text-red-600 font-semibold"
                          : ""
                      }`}
                    >
                      {r.delivered - r.pickedUp}
                    </td>
                  </tr>
                ))}
                {!perBin.length && (
                  <tr>
                    <td className="p-3 text-sm text-gray-500" colSpan={6}>
                      Run a report to see results.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
